import useNormalizationStatus from './useNormalizationStatus'
import useNormalizationUpdatedAt from './useNormalizationUpdatedAt'
import useStoreSubtitle from './useStoreSubtitle'

export type IApiKeyType = keyof typeof api

const api = {
  storeSubtitle: useStoreSubtitle,
  normalizationStatus: useNormalizationStatus,
  normalizationUpdatedAt: useNormalizationUpdatedAt,
}

const apiKeys = Object.keys(api) as (keyof typeof api)[]

const isApiKey = (value: string): value is IApiKeyType =>
  apiKeys.includes(value as IApiKeyType)

const useApi = (apiName?: IApiKeyType | (IApiKeyType | string)[]) => {
  const storeSubtitleResult = useStoreSubtitle()
  const normalizationStatusResult = useNormalizationStatus()
  const normalizationUpdatedAtResult = useNormalizationUpdatedAt()

  const apiResults: Record<
    IApiKeyType,
    { data: string | string[] | null; loading: boolean }
  > = {
    storeSubtitle: storeSubtitleResult,
    normalizationStatus: normalizationStatusResult,
    normalizationUpdatedAt: normalizationUpdatedAtResult,
  }

  if (!apiName) {
    return { data: undefined, loading: false }
  }

  if (!Array.isArray(apiName)) {
    return apiResults[apiName]
  }

  const hasApiKey = apiName.some(isApiKey)

  if (!hasApiKey) {
    return { data: apiName, loading: false }
  }

  const loading = apiName.some(
    (item) => isApiKey(item) && apiResults[item].loading,
  )

  if (loading) {
    return { data: null, loading: true }
  }

  const data = apiName.flatMap((item) => {
    if (isApiKey(item)) {
      const result = apiResults[item].data
      return result ? (Array.isArray(result) ? result : [result]) : []
    }
    return [item]
  })

  return { data, loading: false }
}

export default useApi
